import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface DiscrepancyItemProps {
  discrepancy: {
    id: string;
    payment_method: string;
    expected_amount: number;
    counted_amount: number;
    difference_amount: number;
    explanation: string;
    status: string;
    counting_date: string;
  };
  onStatusUpdate: (id: string, status: string) => void;
  onJustifiedClick: (discrepancy: any) => void;
}

export const DiscrepancyItem = ({
  discrepancy,
  onStatusUpdate,
  onJustifiedClick,
}: DiscrepancyItemProps) => {
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      pending: { label: "En attente", className: "bg-yellow-500" },
      approved: { label: "Approuvé", className: "bg-green-500" },
      deducted: { label: "Retenu sur salaire", className: "bg-blue-500" },
      justified: { label: "Justifié", className: "bg-gray-500" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">{discrepancy.payment_method}</h3>
          <p className="text-sm text-muted-foreground">
            {format(new Date(discrepancy.counting_date), "dd MMMM yyyy", {
              locale: fr,
            })}
          </p>
        </div>
        {getStatusBadge(discrepancy.status)}
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-muted-foreground">Montant attendu</p>
          <p className="font-medium">
            {discrepancy.expected_amount.toLocaleString()} FCFA
          </p>
        </div>
        <div>
          <p className="text-muted-foreground">Montant compté</p>
          <p className="font-medium">
            {discrepancy.counted_amount.toLocaleString()} FCFA
          </p>
        </div>
      </div>

      <div>
        <p className="text-muted-foreground text-sm">Différence</p>
        <p
          className={`font-medium ${
            discrepancy.difference_amount < 0
              ? "text-red-600"
              : "text-green-600"
          }`}
        >
          {discrepancy.difference_amount.toLocaleString()} FCFA
        </p>
      </div>

      <div>
        <p className="text-muted-foreground text-sm">Explication</p>
        <p className="text-sm mt-1">{discrepancy.explanation}</p>
      </div>

      {discrepancy.status === "pending" && (
        <div className="flex gap-2 mt-4">
          <Button
            size="sm"
            onClick={() => onStatusUpdate(discrepancy.id, "approved")}
          >
            Approuver
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => onStatusUpdate(discrepancy.id, "deducted")}
          >
            Déduire du salaire
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onJustifiedClick(discrepancy)}
          >
            Marquer comme justifié
          </Button>
        </div>
      )}
    </div>
  );
};